import React from 'react';
import { ZoomIn, ZoomOut, Magnet } from 'lucide-react';
import { useWaveformTrimmer, MIN_ZOOM, MAX_ZOOM } from '../../hooks/useWaveformTrimmer';

function formatTime(t) {
  if (!isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = (t % 60).toFixed(1).padStart(4, '0');
  return `${m}:${s}`;
}

export default function WaveformTrimmer({ peaks, duration, trimStart, trimEnd, currentTime, isPlaying, onTrimChange, lockLength = false }) {
  const {
    canvasRef, scrollRef, contentRef,
    zoom, snapEnabled, setSnapEnabled,
    handleWheel, zoomIn, zoomOut, startDrag,
    startPct, endPct,
  } = useWaveformTrimmer({ peaks, duration, trimStart, trimEnd, currentTime, isPlaying, onTrimChange, lockLength });

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setSnapEnabled((v) => !v)}
          className={`flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded transition-colors ${
            snapEnabled ? 'text-orange-600 bg-orange-50' : 'text-neutral-400 hover:bg-neutral-200'
          }`}
          data-testid="snap-to-second-toggle"
          title="Snap trim handles to whole seconds"
        >
          <Magnet className="w-3 h-3" /> Snap to second
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="p-1 rounded hover:bg-neutral-200 disabled:opacity-30 disabled:hover:bg-transparent text-neutral-500"
            data-testid="waveform-zoom-out-button"
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="text-[10px] text-neutral-500 font-mono w-7 text-center" data-testid="waveform-zoom-level">
            {zoom.toFixed(0)}x
          </span>
          <button
            type="button"
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="p-1 rounded hover:bg-neutral-200 disabled:opacity-30 disabled:hover:bg-transparent text-neutral-500"
            data-testid="waveform-zoom-in-button"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        onWheel={handleWheel}
        className="relative h-20 rounded-lg bg-neutral-100 overflow-x-auto overflow-y-hidden select-none"
        data-testid="waveform-scroll-container"
      >
        <div ref={contentRef} className="relative h-full" style={{ width: `${zoom * 100}%`, minWidth: '100%' }}>
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
          <div
            onPointerDown={startDrag('move')}
            className="absolute top-0 bottom-0 cursor-grab active:cursor-grabbing touch-none"
            style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
            data-testid="waveform-trim-region-handle"
            title="Drag to move the selected region"
          />
          {!lockLength && (
            <>
              <div
                onPointerDown={startDrag('start')}
                className="absolute top-0 bottom-0 w-6 -ml-3 sm:w-3 sm:-ml-1.5 cursor-ew-resize flex items-center justify-center group touch-none"
                style={{ left: `${startPct}%` }}
                data-testid="waveform-trim-start-handle"
              >
                <div className="w-1 h-full bg-orange-600 rounded-full group-hover:bg-orange-700" />
              </div>
              <div
                onPointerDown={startDrag('end')}
                className="absolute top-0 bottom-0 w-6 -ml-3 sm:w-3 sm:-ml-1.5 cursor-ew-resize flex items-center justify-center group touch-none"
                style={{ left: `${endPct}%` }}
                data-testid="waveform-trim-end-handle"
              >
                <div className="w-1 h-full bg-orange-600 rounded-full group-hover:bg-orange-700" />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-neutral-400 font-mono">
        <span>{formatTime(trimStart)}</span>
        <span>{formatTime(trimEnd)}</span>
      </div>
      <p className="text-[10px] text-neutral-400">
        {lockLength ? 'Drag the highlighted selection to move it.' : 'Drag the highlighted region to move it, or drag either edge to resize.'}
      </p>
    </div>
  );
}
